// Cockpit ARTEKNOS - Tour de contrôle multi-écrans (production)
// Route protégée : /admin/cockpit
// Auth : utilise sessionStorage.admin_session (token JWT admin)
// Données : polling /api/admin/cockpit/snapshot toutes les 10s + WebSocket live
// Carte : Leaflet dark avec markers des livreurs actifs en temps réel

import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { DarkTiles } from "@/components/map/DarkTiles";
import { UserProfileSheet } from "@/components/admin/UserProfileSheet";
import { BroadcastResponsesPanel } from "@/components/admin/BroadcastResponsesPanel";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { roleIcon } from "@/components/map/roleMarkers";

const courierMarker = roleIcon("courier", { size: 34 });
const taxiMarker = roleIcon("taxi", { size: 34 });
import {
  Shield, Users, Store, Bike, Hammer, MapPin, Radio, RefreshCw,
  TrendingUp, Activity, Lock, Bell, ArrowLeft,
} from "lucide-react";
import RainOverrideCard from "@/components/admin/RainOverrideCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// Palette "Tour de Contrôle Néon" — inspirée de la nouvelle DA
const NIGHT_BG = "#0A1E3F";       // bleu nuit profond (background)
const NIGHT_SURFACE = "#0F2654";  // surface cards
const NEON_CYAN = "#22D3EE";      // cyan néon (accents, glows, borders)
const GOLD = "#F5C518";           // or vif (CTA principal)
const NSIC = "#003594";           // bleu NSIC (conservation identité)

const hubIcon = L.divIcon({
  className: "hub-marker",
  html: `<div style="width:44px;height:44px;border-radius:50%;background:${NSIC};border:4px solid ${NEON_CYAN};box-shadow:0 0 24px ${NEON_CYAN}, 0 0 12px ${GOLD}aa;display:flex;align-items:center;justify-content:center;font-size:20px;color:white;font-weight:900">A</div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

const formatFCFA = (n) => (n || 0).toLocaleString("fr-FR");

// Petit format relatif "il y a X min"
function relTime(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  return `il y a ${Math.floor(h / 24)}j`;
}

const STATUS_COLOR = {
  pending: "text-amber-400 bg-amber-500/10 border-amber-500/30",
  preparing: "text-blue-400 bg-blue-500/10 border-blue-500/30",
  ready: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  assigned: "text-indigo-400 bg-indigo-500/10 border-indigo-500/30",
  picked_up: "text-purple-400 bg-purple-500/10 border-purple-500/30",
  in_transit: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  delivered: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
  cancelled: "text-red-400 bg-red-500/10 border-red-500/30",
};

export default function Cockpit() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const closeProfile = useCallback(() => setProfileId(null), []);
  const [loading, setLoading] = useState(true);
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting | online | offline
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  // -------- Auth header --------
  const adminToken = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("admin_session");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.token || null;
    } catch {
      return null;
    }
  }, []);

  // Redirect to login if no token
  useEffect(() => {
    if (!adminToken) {
      toast.error("Session admin requise");
      navigate("/admin-private-126");
    }
  }, [adminToken, navigate]);

  // -------- Fetch snapshot --------
  const fetchSnapshot = useCallback(async () => {
    if (!adminToken) return;
    try {
      const { data } = await axios.get(`${API}/admin/cockpit/snapshot`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setSnapshot(data);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("admin_session");
        navigate("/admin-private-126");
      } else {
        console.warn("[Cockpit] snapshot err:", err.message);
      }
    }
  }, [adminToken, navigate]);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, 10000); // refresh 10s
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  // -------- WebSocket admin live (optionnel — uniquement si endpoint existe) --------
  // Pour éviter des erreurs de connexion répétées, on essaie une seule fois
  // et on retombe sur le polling 10s comme source de vérité.
  useEffect(() => {
    if (!adminToken) return undefined;
    let cancelled = false;

    const tryConnect = () => {
      try {
        const wsProtocol = BACKEND_URL.startsWith("https://") ? "wss://" : "ws://";
        const host = BACKEND_URL.replace(/^https?:\/\//, "");
        const url = `${wsProtocol}${host}/api/ws/admin?token=${encodeURIComponent(adminToken)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        const timeout = setTimeout(() => {
          // si pas d'open en 3s, on bascule en mode polling-only
          if (ws.readyState !== WebSocket.OPEN && !cancelled) {
            setWsStatus("polling");
            try { ws.close(); } catch (err) { console.debug(err); }
          }
        }, 3000);
        ws.onopen = () => {
          clearTimeout(timeout);
          if (!cancelled) setWsStatus("online");
        };
        ws.onmessage = (e) => {
          if (e.data === "pong") return;
          try {
            const msg = JSON.parse(e.data);
            if (!cancelled) setLastEvent({ ...msg, at: new Date().toISOString() });
            if (msg.event && ["order_created", "order_assigned", "order_delivered", "courier_online"].includes(msg.event)) {
              fetchSnapshot();
            }
          } catch (err) {
            console.debug("[Cockpit WS] parse:", err);
          }
        };
        ws.onclose = () => {
          clearTimeout(timeout);
          if (!cancelled) setWsStatus("polling");
        };
        ws.onerror = () => {
          clearTimeout(timeout);
          try { ws.close(); } catch (err) { console.debug(err); }
          if (!cancelled) setWsStatus("polling");
        };
      } catch (e) {
        console.warn("[Cockpit WS] err:", e);
        if (!cancelled) setWsStatus("polling");
      }
    };
    tryConnect();

    return () => {
      cancelled = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch (err) { console.debug(err); } }
    };
  }, [adminToken, fetchSnapshot]);

  // -------- LOADING --------
  if (loading || !snapshot) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center" style={{ background: NIGHT_BG }}>
        <div className="flex items-center gap-3">
          <RefreshCw className="w-5 h-5 animate-spin" style={{ color: NEON_CYAN }} />
          <p className="font-black uppercase tracking-widest text-sm">Initialisation tour de contrôle…</p>
        </div>
      </div>
    );
  }

  const { admin, client, merchant, driver, artisan, users } = snapshot;

  // Position Dakar / hub Yoff
  const hubPosition = [14.7167, -17.4677];

  return (
    <div
      className="min-h-screen text-white font-sans flex flex-col"
      style={{
        background: `radial-gradient(ellipse at top, ${NEON_CYAN}10 0%, ${NIGHT_BG} 40%, #061536 100%)`,
      }}
      data-testid="cockpit-page"
    >
      {/* ============ HEADER COCKPIT ============ */}
      <header
        className="border-b px-6 py-4 flex justify-between items-center shrink-0 z-50"
        style={{
          background: `linear-gradient(180deg, ${NIGHT_SURFACE} 0%, ${NIGHT_BG} 100%)`,
          borderColor: `${NEON_CYAN}33`,
          boxShadow: `0 4px 24px ${NEON_CYAN}22`,
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin-private-126")}
            className="w-10 h-10 rounded-xl border flex items-center justify-center transition-colors hover:scale-105"
            style={{ background: `${NEON_CYAN}15`, borderColor: `${NEON_CYAN}55`, color: NEON_CYAN }}
            data-testid="cockpit-back-btn"
            aria-label="Retour admin"
          >
            <ArrowLeft size={16} />
          </button>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-xl text-white"
            style={{
              background: `linear-gradient(135deg, ${NSIC} 0%, ${NEON_CYAN} 100%)`,
              boxShadow: `0 0 20px ${NEON_CYAN}66`,
            }}
          >
            A
          </div>
          <div>
            <h1 className="text-xl font-black italic tracking-wide text-white">ARTEKNOS · TOUR DE CONTRÔLE</h1>
            <span className="text-[10px] font-bold tracking-widest block -mt-1 uppercase" style={{ color: GOLD }}>
              Cockpit live · 5 espaces unifiés
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 border"
            style={{
              background: `${NEON_CYAN}15`,
              borderColor: `${NEON_CYAN}66`,
              color: NEON_CYAN,
              boxShadow: `0 0 12px ${NEON_CYAN}33`,
            }}
            data-testid="cockpit-online-count"
            title="Utilisateurs connectés en temps réel"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            {users?.online_count || 0} EN LIGNE
          </span>
          <span
            className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 border"
            style={{
              background: wsStatus === "online" ? `${NEON_CYAN}15` : wsStatus === "polling" ? `${NEON_CYAN}10` : "rgba(245,197,24,0.10)",
              borderColor: wsStatus === "online" ? `${NEON_CYAN}88` : wsStatus === "polling" ? `${NEON_CYAN}55` : `${GOLD}55`,
              color: wsStatus === "online" ? NEON_CYAN : wsStatus === "polling" ? NEON_CYAN : GOLD,
              boxShadow: wsStatus !== "connecting" ? `0 0 12px ${NEON_CYAN}33` : "none",
            }}
            data-testid="cockpit-ws-status"
          >
            <span
              className={`w-2 h-2 rounded-full ${wsStatus === "online" ? "animate-ping" : "animate-pulse"}`}
              style={{ background: wsStatus === "connecting" ? GOLD : NEON_CYAN }}
            />
            {wsStatus === "online" ? "COUPLAGE ACTIF" : wsStatus === "polling" ? "POLLING 10S" : "CONNEXION…"}
          </span>
          <button
            onClick={fetchSnapshot}
            className="px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 transition-all hover:scale-105"
            style={{
              background: `${GOLD}15`,
              borderColor: `${GOLD}55`,
              color: GOLD,
              boxShadow: `0 0 12px ${GOLD}33`,
            }}
            data-testid="cockpit-refresh-btn"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </header>

      {/* ============ COCKPIT GRID ============ */}
      <main className="flex-grow grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4 overflow-y-auto">
        {/* Mode Pluie Dakar - toggle manuel */}
        <RainOverrideCard />

        {/* CARD 1 — ADMIN */}
        <Card
          icon={<Shield size={18} style={{ color: GOLD }} />}
          title="1. Admin Global"
          subtitle="Coffre-fort financier"
          accent={GOLD}
          testId="cockpit-card-admin"
        >
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Stat label="Revenue total" value={`${formatFCFA(admin.revenue)} F`} color="text-white" />
            <Stat label="Escrow gelé" value={`${formatFCFA(admin.escrow)} F`} color="text-[#F5C518]" />
            <Stat label="Commissions" value={`${formatFCFA(admin.commission)} F`} color="text-[#22D3EE]" />
            <Stat label="Payouts livreurs" value={`${formatFCFA(admin.payouts)} F`} color="text-[#22D3EE]" />
          </div>
          <div
            className="flex-1 rounded-xl p-3 text-[10px] font-mono overflow-y-auto border"
            style={{ background: `${NIGHT_BG}cc`, borderColor: `${GOLD}33`, color: `${NEON_CYAN}99` }}
            data-testid="cockpit-admin-logs"
          >
            {admin.recent_disputes.length === 0 ? (
              <p style={{ color: NEON_CYAN }}>[live] aucun litige actif — système nominal</p>
            ) : (
              admin.recent_disputes.map((d) => (
                <p key={d.id} className="truncate" style={{ color: GOLD }}>
                  [LITIGE] #{d.id?.substring(0, 8)} · order {d.order_id?.substring(0, 8)} · {d.status}
                </p>
              ))
            )}
            {lastEvent && (
              <p className="mt-1 truncate" style={{ color: NEON_CYAN }}>[WS] {lastEvent.event} · {relTime(lastEvent.at)}</p>
            )}
          </div>
        </Card>

        {/* CARD 2 — CLIENT */}
        <Card
          icon={<Users size={18} style={{ color: NEON_CYAN }} />}
          title="2. Espace Client"
          subtitle={`${client.last_24h_count} commandes (24h)`}
          accent={NEON_CYAN}
          testId="cockpit-card-client"
        >
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {client.recent_orders.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: `${NEON_CYAN}77` }}>Aucune commande dans les 24h</p>
            ) : (
              client.recent_orders.map((o) => (
                <div
                  key={o.id}
                  className="p-2.5 rounded-xl text-xs border"
                  style={{ background: `${NEON_CYAN}08`, borderColor: `${NEON_CYAN}33` }}
                  data-testid={`cockpit-client-order-${o.id}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${STATUS_COLOR[o.status] || "text-zinc-400 border-zinc-700"}`}>
                      {o.status}
                    </span>
                    <span className="text-[10px] font-mono" style={{ color: `${NEON_CYAN}99` }}>{relTime(o.created_at)}</span>
                  </div>
                  <p className="text-white font-bold truncate">{o.customer_name}</p>
                  <p className="text-[10px] truncate" style={{ color: `${NEON_CYAN}aa` }}>{o.delivery_address}</p>
                  <p className="text-[10px] font-bold mt-0.5" style={{ color: GOLD }}>{formatFCFA(o.total_amount)} F</p>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* CARD 3 — MERCHANT */}
        <Card
          icon={<Store size={18} style={{ color: GOLD }} />}
          title="3. Commerçants"
          subtitle={`${merchant.pending_orders.length} en préparation`}
          accent={GOLD}
          testId="cockpit-card-merchant"
        >
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {merchant.pending_orders.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: `${GOLD}77` }}>Aucune commande en préparation</p>
            ) : (
              merchant.pending_orders.map((o) => (
                <div
                  key={o.id}
                  className="p-2.5 rounded-xl text-xs border"
                  style={{ background: `${GOLD}08`, borderColor: `${GOLD}33` }}
                  data-testid={`cockpit-merchant-order-${o.id}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${STATUS_COLOR[o.status]}`}>
                      {o.status}
                    </span>
                    <span className="text-[10px] font-bold" style={{ color: GOLD }}>{formatFCFA(o.items_amount || o.total_amount)} F</span>
                  </div>
                  <p className="text-white font-bold truncate">{o.customer_name}</p>
                  <p className="text-[10px] truncate" style={{ color: `${GOLD}aa` }}>{o.delivery_address}</p>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* CARD 4 — MAP LEAFLET DARK LIVE */}
        <section
          className="rounded-3xl p-4 flex flex-col gap-3 relative overflow-hidden h-[440px] md:col-span-2 xl:col-span-1 border"
          style={{
            background: `linear-gradient(180deg, ${NIGHT_SURFACE}ee 0%, ${NIGHT_BG}cc 100%)`,
            borderColor: `${NEON_CYAN}66`,
            boxShadow: `0 0 32px ${NEON_CYAN}33, inset 0 0 30px ${NEON_CYAN}15`,
          }}
          data-testid="cockpit-card-map"
        >
          <div className="flex justify-between items-center shrink-0">
            <div className="flex items-center gap-2">
              <MapPin size={16} style={{ color: NEON_CYAN }} />
              <h3 className="text-sm font-black text-white uppercase tracking-wide">Suivi GPS · Carte Live</h3>
            </div>
            <span
              className="text-[9px] font-mono uppercase px-2 py-0.5 rounded border animate-pulse"
              style={{ color: NEON_CYAN, background: `${NEON_CYAN}10`, borderColor: `${NEON_CYAN}55` }}
            >
              {driver.active_count} livreurs · {driver.in_route_count} en route
            </span>
          </div>
          <div className="flex-grow rounded-2xl overflow-hidden relative border" style={{ borderColor: `${NEON_CYAN}33` }}>
            <MapContainer
              center={hubPosition}
              zoom={12}
              scrollWheelZoom={true}
              className="woma-dark-map"
              style={{ width: "100%", height: "100%" }}
              attributionControl={false}
              data-testid="cockpit-leaflet-map"
            >
              <DarkTiles />
              <Marker position={hubPosition} icon={hubIcon}>
                <Popup>
                  <b>ARTEKNOS Hub</b>
                  <br />Yoff, Dakar — Sénégal
                </Popup>
              </Marker>
              {driver.active_couriers.map((c) => {
                if (!c.last_latitude || !c.last_longitude) return null;
                return (
                  <Marker
                    key={c.id}
                    position={[c.last_latitude, c.last_longitude]}
                    icon={c.driver_type === "taxi" ? taxiMarker : courierMarker}
                  >
                    <Popup>
                      <b>{c.full_name}</b>
                      <br />
                      {c.driver_type === "taxi" ? "Chauffeur taxi" : c.vehicle_type || "Livreur"} · ⭐ {(c.rating || 5).toFixed(1)}
                      <br />
                      📞 {c.phone}
                      <br />
                      <i>Mis à jour : {relTime(c.last_location_update)}</i>
                    </Popup>
                  </Marker>
                );
              })}
              <CircleMarker
                center={hubPosition}
                radius={22}
                pathOptions={{ color: NEON_CYAN, fillColor: NEON_CYAN, fillOpacity: 0.18, weight: 1 }}
              />
            </MapContainer>
          </div>
        </section>

        {/* CARD 5 — DRIVER */}
        <Card
          icon={<Bike size={18} style={{ color: NEON_CYAN }} />}
          title="4. Livreurs"
          subtitle={`${driver.active_count} en ligne · ${driver.in_route_count} en route`}
          accent={NEON_CYAN}
          testId="cockpit-card-driver"
        >
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {driver.active_couriers.length === 0 ? (
              <p className="text-xs text-center py-6" style={{ color: `${NEON_CYAN}77` }}>Aucun livreur en ligne</p>
            ) : (
              driver.active_couriers.map((c) => (
                <div
                  key={c.id}
                  className="p-2.5 rounded-xl flex items-center gap-3 border"
                  style={{ background: `${NEON_CYAN}08`, borderColor: `${NEON_CYAN}44` }}
                  data-testid={`cockpit-driver-${c.id}`}
                >
                  <div
                    className="w-9 h-9 rounded-xl border flex items-center justify-center"
                    style={{ background: `${NEON_CYAN}15`, borderColor: `${NEON_CYAN}66`, color: NEON_CYAN, boxShadow: `0 0 12px ${NEON_CYAN}44` }}
                  >
                    <Bike size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <button type="button" onClick={() => setProfileId(c.id)} className="text-xs font-black text-white truncate hover:underline block max-w-full text-left" data-testid={`cockpit-driver-name-${c.id}`}>{c.full_name}</button>
                    <p className="text-[10px] font-bold truncate" style={{ color: `${NEON_CYAN}cc` }}>
                      {c.vehicle_type || "Moto"} · ⭐ {(c.rating || 5).toFixed(1)} · 📞 {c.phone}
                    </p>
                    {c.last_location_update && (
                      <p className="text-[9px] font-mono" style={{ color: GOLD }}>GPS {relTime(c.last_location_update)}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* CARD 6 — ARTISAN */}
        <Card
          icon={<Hammer size={18} style={{ color: GOLD }} />}
          title="5. Artisans Pro"
          subtitle="Wave Escrow projets"
          accent={GOLD}
          testId="cockpit-card-artisan"
        >
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Stat label="En attente" value={artisan.pending_count} color="text-[#F5C518]" />
            <Stat label="En cours" value={artisan.in_progress_count} color="text-[#22D3EE]" />
          </div>
          <div
            className="p-3 rounded-xl border space-y-1.5"
            style={{ background: `${GOLD}10`, borderColor: `${GOLD}44` }}
          >
            <div className="flex justify-between items-center text-[10px]">
              <span className="uppercase tracking-widest" style={{ color: `${GOLD}cc` }}>Budget gelé Escrow</span>
              <span className="font-bold" style={{ color: NEON_CYAN }}>ACTIF</span>
            </div>
            <p className="text-2xl font-black tracking-tight" style={{ color: GOLD }}>
              {formatFCFA(artisan.pending_total + artisan.in_progress_total)}
              <span className="text-xs ml-1" style={{ color: `${GOLD}99` }}>FCFA</span>
            </p>
            <p className="text-[10px]" style={{ color: `${NEON_CYAN}99` }}>Fonds libérés après signature de fin de chantier.</p>
          </div>
        </Card>

        {/* CARD 7 — UTILISATEURS CONNECTÉS TEMPS RÉEL (full width) */}
        <section
          className="rounded-3xl p-5 flex flex-col gap-3 relative overflow-hidden md:col-span-2 xl:col-span-3 border"
          style={{
            background: `linear-gradient(180deg, ${NIGHT_SURFACE}ee 0%, ${NIGHT_BG}cc 100%)`,
            borderColor: `${NEON_CYAN}55`,
            boxShadow: `0 0 32px ${NEON_CYAN}22, inset 0 0 30px ${NEON_CYAN}10`,
          }}
          data-testid="cockpit-card-users"
        >
          <div className="absolute top-0 right-0 w-60 h-60 rounded-full blur-3xl pointer-events-none" style={{ background: `${NEON_CYAN}15` }} />
          <div className="flex justify-between items-start border-b pb-2 shrink-0 relative" style={{ borderColor: `${NEON_CYAN}33` }}>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-black text-white tracking-wide uppercase flex items-center gap-2">
                <Activity size={18} style={{ color: NEON_CYAN }} />
                6. Utilisateurs ARTEKNOS
                <span className="ml-2 px-2 py-0.5 rounded-full text-[9px] font-mono" style={{ background: "#10B98115", color: "#34D399", border: "1px solid #10B98155" }}>
                  ● {users?.online_count || 0} EN LIGNE
                </span>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-mono" style={{ background: `${NEON_CYAN}10`, color: NEON_CYAN, border: `1px solid ${NEON_CYAN}44` }}>
                  {users?.total || 0} TOTAL
                </span>
              </h2>
              <p className="text-[10px] font-bold uppercase tracking-widest mt-0.5" style={{ color: `${NEON_CYAN}99` }}>
                Présence temps réel — Polling WebSocket toutes les 10s
              </p>
            </div>
          </div>

          {/* Stats par rôle */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 shrink-0">
            <RoleStat label="Clients" value={users?.by_role?.client || 0} icon={<Users size={12} />} />
            <RoleStat label="Livreurs" value={users?.by_role?.courier || 0} icon={<Bike size={12} />} />
            <RoleStat label="Commerçants" value={users?.by_role?.partner || 0} icon={<Store size={12} />} />
            <RoleStat label="Vendeurs" value={users?.by_role?.vendor || 0} icon={<Store size={12} />} />
            <RoleStat label="Artisans" value={users?.by_role?.artisan || 0} icon={<Hammer size={12} />} />
            <RoleStat label="Admin" value={users?.by_role?.admin || 0} icon={<Shield size={12} />} />
          </div>

          {/* Liste users */}
          <div className="flex-1 overflow-y-auto pr-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="cockpit-users-list">
            {(!users?.list || users.list.length === 0) ? (
              <p className="col-span-full text-xs text-center py-6" style={{ color: `${NEON_CYAN}77` }}>Aucun utilisateur enregistré</p>
            ) : (
              users.list.map((u) => <UserRow key={u.id} user={u} onOpen={setProfileId} />)
            )}
          </div>
        </section>
      </main>
      <div className="px-4 md:px-6 pb-6"><BroadcastResponsesPanel onOpenUser={setProfileId} /></div>

      {/* ============ FOOTER LIVE ENGINE ============ */}
      <footer
        className="border-t px-4 py-3 flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 shrink-0"
        style={{
          background: `linear-gradient(0deg, ${NIGHT_SURFACE} 0%, ${NIGHT_BG} 100%)`,
          borderColor: `${NEON_CYAN}33`,
          boxShadow: `0 -4px 24px ${NEON_CYAN}22`,
        }}
      >
        <div className="flex items-center gap-3">
          <Radio size={12} className="animate-pulse" style={{ color: NEON_CYAN }} />
          <p className="text-[10px] font-mono" style={{ color: `${NEON_CYAN}cc` }}>
            ARTEKNOS-CORE-ENGINE · {wsStatus === "online" ? "Listening real-time cross-account transactions" : "Polling 10s actif"}
          </p>
        </div>
        <div className="text-[10px] font-mono" style={{ color: `${GOLD}88` }}>
          Snapshot : {snapshot.timestamp ? relTime(snapshot.timestamp) : "—"} · Refresh 10s · v2026
        </div>
      </footer>
      <UserProfileSheet userId={profileId} onClose={closeProfile} onSaved={fetchSnapshot} />
    </div>
  );
}

// ---------- SOUS-COMPOSANTS ----------

function Card({ icon, title, subtitle, accent, testId, children }) {
  return (
    <section
      className="rounded-3xl p-5 flex flex-col gap-3 relative overflow-hidden h-[440px] border"
      style={{
        background: `linear-gradient(180deg, ${NIGHT_SURFACE}ee 0%, ${NIGHT_BG}cc 100%)`,
        borderColor: `${accent}55`,
        boxShadow: `0 0 32px ${accent}22, inset 0 0 30px ${accent}10`,
      }}
      data-testid={testId}
    >
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none" style={{ background: `${accent}22` }} />
      <div className="flex justify-between items-start border-b pb-2 shrink-0 relative" style={{ borderColor: `${accent}33` }}>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-black text-white tracking-wide uppercase flex items-center gap-2">
            {icon} {title}
          </h2>
          <p className="text-[10px] font-bold uppercase tracking-widest mt-0.5 truncate" style={{ color: `${accent}cc` }}>
            {subtitle}
          </p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div
      className="p-2.5 rounded-xl border"
      style={{
        background: `${NEON_CYAN}08`,
        borderColor: `${NEON_CYAN}33`,
      }}
    >
      <p className="text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: `${NEON_CYAN}` }}>{label}</p>
      <p className={`text-base font-black tracking-tight ${color} truncate`}>{value}</p>
    </div>
  );
}

function RoleStat({ label, value, icon }) {
  return (
    <div
      className="p-2 rounded-xl border flex items-center gap-2"
      style={{ background: `${NEON_CYAN}08`, borderColor: `${NEON_CYAN}33` }}
    >
      <span className="opacity-70" style={{ color: NEON_CYAN }}>{icon}</span>
      <div className="min-w-0">
        <p className="text-[9px] font-black uppercase tracking-widest leading-tight" style={{ color: `${NEON_CYAN}cc` }}>{label}</p>
        <p className="text-base font-black text-white leading-none">{value}</p>
      </div>
    </div>
  );
}

const ROLE_COLORS = {
  client: { color: "#22D3EE", bg: "#22D3EE15", border: "#22D3EE55" },
  courier: { color: "#22D3EE", bg: "#22D3EE15", border: "#22D3EE55" },
  partner: { color: "#F5C518", bg: "#F5C51815", border: "#F5C51855" },
  vendor: { color: "#F5C518", bg: "#F5C51815", border: "#F5C51855" },
  artisan: { color: "#A78BFA", bg: "#A78BFA15", border: "#A78BFA55" },
  admin: { color: "#F5C518", bg: "#F5C51815", border: "#F5C51855" },
};

function relativeShort(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}j`;
}

function UserRow({ user, onOpen }) {
  const roleStyle = ROLE_COLORS[user.role] || ROLE_COLORS.client;
  const initial = (user.full_name || user.email || "?").charAt(0).toUpperCase();
  return (
    <div
      className="flex items-center gap-2.5 p-2.5 rounded-xl border transition-colors hover:scale-[1.01]"
      style={{
        background: user.online ? "#10B98108" : `${roleStyle.bg}`,
        borderColor: user.online ? "#10B98155" : roleStyle.border,
      }}
      data-testid={`cockpit-user-${user.id}`}
    >
      <div className="relative shrink-0">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm text-white"
          style={{
            background: `linear-gradient(135deg, ${roleStyle.color}88 0%, ${roleStyle.color} 100%)`,
            boxShadow: user.online ? `0 0 12px #10B981aa` : `0 0 8px ${roleStyle.color}66`,
          }}
        >
          {initial}
        </div>
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${user.online ? "animate-pulse" : ""}`}
          style={{
            background: user.online ? "#10B981" : "#475569",
            borderColor: user.online ? "#10B981" : "#1E293B",
            boxShadow: user.online ? "0 0 8px #10B981" : "none",
          }}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => onOpen(user.id)} className="text-xs font-black text-white truncate hover:underline text-left" data-testid={`cockpit-user-name-${user.id}`}>{user.full_name || "—"}</button>
          <span
            className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded shrink-0"
            style={{ color: roleStyle.color, background: roleStyle.bg, border: `1px solid ${roleStyle.border}` }}
          >
            {user.role}
          </span>
          {user.is_approved === false && (
            <span className="text-[8px] font-black uppercase px-1.5 py-0.5 rounded shrink-0" style={{ color: "#F5C518", background: "#F5C51815", border: "1px solid #F5C51855" }} title="Non approuvé">
              PEND
            </span>
          )}
        </div>
        <p className="text-[10px] truncate" style={{ color: `${NEON_CYAN}99` }}>
          {user.phone || user.email || "—"}
        </p>
      </div>
      <span
        className="text-[9px] font-mono shrink-0"
        style={{ color: user.online ? "#34D399" : `${NEON_CYAN}66` }}
        title={user.online ? "Connecté maintenant" : `Vu ${relativeShort(user.last_seen || user.created_at)}`}
      >
        {user.online ? "● LIVE" : `· ${relativeShort(user.last_seen || user.created_at)}`}
      </span>
    </div>
  );
}
